import { MutedAlert } from '@app/components/MutedAlert';
import { Station } from '@mi-front/core';
import { FileInput } from '@mi-front/ui';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Button } from '@mui/material';
import { UseMutationResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { validateAndUploadFileViewMessages } from './validate-and-upload-file-view-messages';

/**
 * Payload shape used for both validate (dryRun: 'true') and upload (dryRun: 'false') API calls.
 * API supports multiple files in a single request.
 */
export type UploadMutationVariables = {
  data: { files: Blob[]; stationId: string; dryRun: string };
};

/**
 * Reusable validate-then-upload flow: file input (multiple) → validate → show preview + upload/cancel → success + preview.
 * Used by BCR, LTX, and AVG upload screens that share the same UI and flow with different APIs and previews.
 * useUploadMutation can be any hook that returns a mutation with mutate({ data: { files, stationId, dryRun } }).
 * TVariables should match the hook's mutation variables (e.g. { data: BulkUploadBcrFilesMutationRequest }) so
 * auditSupport.useBulkUploadBcrFiles, useBulkUploadLtxFiles, useBulkUploadAvgFiles can be passed without casts.
 */
/** Minimal validation result shape to read entries count (BCR/LTX/AVG APIs). */
type EntriesCountData = { entriesCount?: number; entries?: unknown[] };

export function ValidateAndUploadFileView<TData, TVariables = UploadMutationVariables>({
  station,
  accept,
  fileType,
  useUploadMutation,
  renderPreview,
}: {
  station: Station;
  accept: string;
  /** File type label (e.g. "BCR", "LTX", "AVG") used for file input and confirm message. */
  fileType: string;
  useUploadMutation: () => UseMutationResult<TData, unknown, TVariables>;
  renderPreview: (data: TData) => React.ReactNode;
}) {
  const { $t } = useIntl();
  const [files, setFiles] = useState<File[]>([]);

  const validateMutation = useUploadMutation();
  const uploadMutation = useUploadMutation();

  function payload(fileList: File[], dryRun: string): UploadMutationVariables {
    return { data: { files: fileList, stationId: station.id, dryRun } };
  }

  function handleFileChange(fileList: File[]) {
    if (fileList.length > 0) {
      setFiles(fileList);
      validateMutation.mutate(payload(fileList, 'true') as TVariables);
    }
  }

  function handleUpload() {
    if (files.length === 0) return;
    uploadMutation.mutate(payload(files, 'false') as TVariables);
  }

  function handleReset() {
    setFiles([]);
    validateMutation.reset();
    uploadMutation.reset();
  }

  const hasValidationResult = validateMutation.isSuccess && validateMutation.data;
  const hasUploadResult = uploadMutation.isSuccess && uploadMutation.data;

  const validationData = validateMutation.data as EntriesCountData | undefined;
  const entriesCount =
    validationData?.entriesCount ??
    (Array.isArray(validationData?.entries) ? validationData.entries.length : 0);

  const messages = validateAndUploadFileViewMessages;

  return (
    <Box>
      {!hasValidationResult && !hasUploadResult && (
        <Box>
          <FileInput
            loading={validateMutation.isPending}
            name="files"
            multiple
            className="mr-2"
            onChange={handleFileChange}
            label={$t(messages.selectFileLabel, { type: fileType })}
            accept={accept}
            startIcon={<AttachFileIcon fontSize="small" />}
          />
          {validateMutation.isError && (
            <Alert severity="error" sx={{ fontSize: 'medium', my: 2 }}>
              {$t(messages.validateError)} {(validateMutation as { error: Error }).error?.message}
            </Alert>
          )}
        </Box>
      )}

      {hasValidationResult && !hasUploadResult && (
        <Box>
          <Box mb={2}>
            <strong>
              {files.length === 1 ? $t(messages.fileLabel) : $t(messages.filesLabel)}{' '}
            </strong>
            {files.length === 1 ? files[0]?.name : files.map((f) => f.name).join(', ')}
          </Box>

          <MutedAlert icon={false} sx={{ mb: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              {$t(messages.entriesFoundToLoad, { n: entriesCount, type: fileType })}
            </Box>
            <Box
              component="span"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
            >
              <Button
                variant="outlined"
                color="success"
                onClick={handleUpload}
                disabled={uploadMutation.isPending}
                startIcon={<UploadIcon />}
              >
                {uploadMutation.isPending
                  ? $t(messages.uploading)
                  : files.length === 1
                    ? $t(messages.uploadFile)
                    : $t(messages.uploadFiles)}
              </Button>
              {!uploadMutation.isPending && (
                <Button
                  color="warning"
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<CancelIcon />}
                >
                  {$t(messages.cancel)}
                </Button>
              )}
            </Box>
          </MutedAlert>

          {validateMutation.isError && (
            <Alert severity="error" sx={{ fontSize: 'medium', my: 2 }}>
              {$t(messages.validateError)} {(validateMutation as { error: Error }).error?.message}
            </Alert>
          )}

          {uploadMutation.isError && (
            <Alert severity="error" sx={{ fontSize: 'medium', my: 2 }}>
              {$t(messages.uploadError)} {(uploadMutation.error as Error)?.message}
            </Alert>
          )}

          {validateMutation.data && renderPreview(validateMutation.data)}
        </Box>
      )}

      {hasUploadResult && uploadMutation.data && (
        <Box>
          <Box mb={2}>
            <strong>
              {files.length === 1 ? $t(messages.fileLabel) : $t(messages.filesLabel)}{' '}
            </strong>
            {files.length === 1 ? files[0]?.name : files.map((f) => f.name).join(', ')}
          </Box>

          <Alert severity="success" sx={{ fontSize: 'medium', mb: 2 }}>
            {files.length === 1 ? $t(messages.uploadSuccess) : $t(messages.uploadSuccessMultiple)}
            <Box mt={2}>
              <Button color="inherit" variant="outlined" size="small" onClick={handleReset}>
                {$t(messages.processNewFile)}
              </Button>
            </Box>
          </Alert>

          {renderPreview(uploadMutation.data)}
        </Box>
      )}
    </Box>
  );
}
